import z from "zod";

import { HOURS, MINUTES, SECONDS } from "../utils/time";

export const datasetStatusEnum = z.enum(["IDLE", "RUNNING", "FAILED"]);
export type DatasetStatus = z.infer<typeof datasetStatusEnum>;

/** Server-side: how long a completed run is considered fresh for dedup (skip re-execution). */
export const RUN_FRESHNESS_TIMEOUT_MS = 5 * MINUTES;

/** Client-side: how long before displayed dataset results show a staleness indicator. */
export const DATASET_STALENESS_TTL_MS = 24 * HOURS;

/** Published data older than this triggers an automatic background refresh. */
export const PUBLISHED_FRESHNESS_TTL_MS = 1 * HOURS;

/**
 * Minimum interval between manual payload refresh requests for the same
 * organization+dataset pair.
 */
export const MANUAL_PAYLOAD_REFRESH_COOLDOWN_MS = 60 * SECONDS;

/** Maximum rows returned by a draft preview query. */
export const DRAFT_ROW_LIMIT = 1_000;

/**
 * Ceiling on a stored run's Arrow object for every **buffered** execution —
 * drafts, chat, MCP, validation, re-execution. It is deliberately lower than
 * {@link MAX_PUBLISH_RESULT_BYTES}: a buffered caller holds the whole result in
 * server memory before it can even measure it, so the check after serialization
 * is a fail-closed backstop rather than real protection.
 */
export const MAX_RESULT_BYTES = 512 * 1024 * 1024;

/**
 * Ceiling on a **streamed** dataset publish, which can afford to be far higher:
 * rows flow from the driver into the upload one batch at a time, so peak memory
 * tracks a batch rather than the result, and the count is enforced mid-transfer
 * (abandoning the upload and deleting the partial object) instead of after the
 * memory is already spent.
 *
 * The browser is the other side of this number — a run this large is downloaded
 * into OPFS and read by DuckDB as a file handle, never held in the JS heap.
 */
export const MAX_PUBLISH_RESULT_BYTES = 2 * 1024 * 1024 * 1024;

/** Client-side: draft results older than this are considered stale. */
export const DRAFT_STALENESS_MS = 30 * SECONDS;

/** Server-side: max wall-clock time for a draft query before auto-cancel. */
export const DRAFT_QUERY_TIMEOUT_MS = 60 * SECONDS;
